import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import GlassCard from "./GlassCard";

const FeatureTrack = () => {
    return (
        <section className="relative py-24 w-full overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 -z-10">
                <div className="absolute top-1/3 right-0 w-96 h-96 bg-optibiz-orange/15 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
                <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-optibiz-blue/10 rounded-full blur-3xl animate-blob"></div>
            </div>

            <div className="container mx-auto px-4 md:px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
                    <div className="relative pl-0 md:pl-8 animate-slide-in-left">
                        {/* Main Image/UI Mockup */}
                        <GlassCard className="rounded-[2.5rem] p-8 relative z-10 transform transition-all duration-500 md:hover:scale-105 md:hover:-rotate-1">
                            <div className="flex items-center justify-between mb-8">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-optibiz-blue to-optibiz-green overflow-hidden border-2 border-white dark:border-gray-600">
                                        <img src="https://i.pravatar.cc/150?u=5" alt="User" className="w-full h-full object-cover" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-base text-tasktide-dark dark:text-white">Daily Standup</h4>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">10:00 AM - 10:30 AM</p>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-5">
                                <div className="flex items-center gap-4 p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700/50 dark:to-gray-600/30 rounded-2xl border border-gray-100 dark:border-gray-600 group hover:border-optibiz-blue/30 transition-colors">
                                    <div className="w-10 h-10 rounded-full bg-optibiz-blue/20 dark:bg-optibiz-blue/30 flex items-center justify-center text-optibiz-blue group-hover:scale-110 transition-transform">
                                        <CheckCircle2 size={20} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="h-2.5 bg-gray-300 dark:bg-gray-600 rounded-full w-3/4 mb-2.5"></div>
                                        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full w-1/2"></div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4 p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700/50 dark:to-gray-600/30 rounded-2xl border border-gray-100 dark:border-gray-600 group hover:border-optibiz-orange/30 transition-colors">
                                    <div className="w-10 h-10 rounded-full bg-optibiz-orange/20 dark:bg-optibiz-orange/30 flex items-center justify-center text-optibiz-orange group-hover:scale-110 transition-transform">
                                        <CheckCircle2 size={20} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="h-2.5 bg-gray-300 dark:bg-gray-600 rounded-full w-2/3 mb-2.5"></div>
                                        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full w-1/3"></div>
                                    </div>
                                </div>
                            </div>
                        </GlassCard>

                        {/* Decorative background element */}
                        <div className="absolute top-12 left-0 w-full h-full bg-gradient-to-br from-optibiz-yellow/30 to-optibiz-orange/20 dark:from-optibiz-yellow/15 dark:to-optibiz-orange/10 rounded-[2.5rem] -z-10 transform rotate-[4deg] blur-2xl"></div>

                        {/* Floating Badge */}
                        <div className="absolute -top-6 right-2 sm:-right-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl p-4 rounded-2xl shadow-2xl animate-bounce-soft z-20 border border-white/30 dark:border-gray-700/50">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                                <span className="text-xs font-bold text-gray-700 dark:text-gray-300">Live Meeting</span>
                            </div>
                        </div>
                    </div>

                    <div className="animate-slide-in-right">
                        <h2 className="text-4xl md:text-5xl font-extrabold text-tasktide-dark dark:text-white mb-8 leading-tight">
                            Streamline Operations with <span className="gradient-black">Integrated Tools</span>
                        </h2>
                        <p className="text-gray-500 dark:text-gray-400 text-lg mb-10 font-medium leading-relaxed">
                            OptiBiz Suite brings all your business operations into one unified platform. Track sales, manage inventory, handle HR, and process finances—all in real-time with powerful analytics.
                        </p>

                        <ul className="space-y-5 mb-10">
                            {[
                                "Real-time operational visibility across all departments",
                                "Centralized data for better decision-making",
                                "Automated workflows to reduce manual tasks",
                                "Comprehensive reporting and business insights"
                            ].map((item, index) => (
                                <li key={index} className="flex items-center gap-4 group transition-transform md:hover:translate-x-2">
                                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-optibiz-green to-emerald-500 flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform">
                                        <CheckCircle2 size={16} strokeWidth={3} />
                                    </div>
                                    <span className="text-gray-700 dark:text-gray-300 text-md">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FeatureTrack;
